import { Box } from "@mui/material";
import { useTranslation } from "react-i18next";
import type { EmployeeMatchCounts } from "../useAttemptsFilter";
import { EMPLOYEE_MATCH_ATTENTION_STATES, EMPLOYEE_MATCH_DISPLAY } from "./employeeMatchDisplay";

type Props = {
	counts: EmployeeMatchCounts;
	dataTestId: string;
};

/**
 * The needs-attention tail of the preview header's row count, e.g. ` · 12 unmatched · 3 merged`. Renders
 * nothing when every row matched (and `matched` is never counted), so a file with a clean roster resolution
 * — and any CSV without the column at all — keeps the header exactly as it was.
 *
 * Each cell's testid suffix is the stable `state` name rather than the translated label, so it stays the
 * same across locales.
 */
export function EmployeeMatchSummary({ counts, dataTestId }: Props) {
	const { t } = useTranslation();
	const flagged = EMPLOYEE_MATCH_ATTENTION_STATES.filter((state) => counts[state] > 0);
	if (flagged.length === 0) return null;

	return (
		<Box component="span" data-testid={dataTestId}>
			{flagged.map((state) => {
				const { textColor, summaryLabelKey } = EMPLOYEE_MATCH_DISPLAY[state];
				const summaryLabel = t(summaryLabelKey);
				return (
					<Box component="span" key={state} sx={{ color: textColor }} data-testid={`${dataTestId}-${state}`}>
						{` · ${counts[state]} ${summaryLabel}`}
					</Box>
				);
			})}
		</Box>
	);
}
